// src/features/task/TaskTypeTransitionsEditor.tsx
// The transition graph of a task type: which status moves are allowed, and what
// each move demands before it runs.
//
// Presentation: one collapsed row per rule, read as "From → A · B · C". Read
// top to bottom the rows *are* the adjacency list of the graph, which is the
// form a state machine of this size is legible in — an 8×8 matrix has 64 cells
// of which most are empty, and it has nowhere to put the per-transition rules
// (required fields, roles, approval) that are the point of the feature. What a
// list cannot show — dead ends and states nothing leads to — is computed and
// shown above it instead of left for the reader to trace by hand.

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useI18n, useTaskCustomField } from '@react/hooks';
import { Alert, Badge, Button } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PipelinePicker } from '@/components/PipelinePicker';
import { FormRow } from '@/components/form';
import { ChipMultiSelect } from '@/features/task/ChipMultiSelect';
import { makeEntity } from '@/features/task/panelHelpers';
import { useScope } from '@/hooks/useScope';
import type { TaskStatusDefinition, TaskTransitionRule } from '@core/types/task';
import {
  BASE_TASK_STATUSES,
  TRANSITION_ROLES,
  TRANSITION_TASK_FIELDS,
  analyzeTransitions,
  defaultTransitionRules,
  emptyTransitionRule,
  statusLabelKey,
  statusesInCategory,
} from '@/features/task/taskLifecycle';

export interface TaskTypeTransitionsEditorProps {
  value: TaskTransitionRule[];
  onChange: (next: TaskTransitionRule[]) => void;
  /** Custom statuses of the same type — shown against the base status they map to. */
  statusDefinitions: TaskStatusDefinition[];
  initialStatus: string;
  completionStatus: string;
  /** Custom field definition ids enabled on this type; offered as required fields. */
  customFieldIds: string[];
  enableCustomFields: boolean;
  disabled?: boolean;
}

export const TaskTypeTransitionsEditor = observer(
  ({
    value,
    onChange,
    statusDefinitions,
    initialStatus,
    completionStatus,
    customFieldIds,
    enableCustomFields,
    disabled,
  }: TaskTypeTransitionsEditorProps) => {
    const { t } = useI18n();
    const scope = useScope();
    const customFieldStore = useTaskCustomField();

    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<number | null>(null);
    const [confirmFree, setConfirmFree] = useState(false);

    useEffect(() => {
      if (customFieldStore.items.length === 0 && !customFieldStore.isLoading) {
        customFieldStore.ListCustomFieldDefinitions();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isStrict = value.length > 0;

    const analysis = useMemo(
      () => analyzeTransitions(value, initialStatus, completionStatus),
      [value, initialStatus, completionStatus],
    );

    const statusOptions = useMemo(
      () => BASE_TASK_STATUSES.map((status) => ({ value: status, label: t(statusLabelKey(status)) })),
      [t],
    );

    const label = (status: string) => (statusLabelKey(status) ? t(statusLabelKey(status)) : status);

    const fieldOptions = useMemo(
      () => TRANSITION_TASK_FIELDS.map((field) => ({ value: field, label: field })),
      [],
    );

    const customFieldOptions = useMemo(() => {
      if (!enableCustomFields) return [];
      const ids = new Set(customFieldIds);
      return customFieldStore.items
        .filter((def) => ids.has(def.entity?.id || ''))
        .map((def) => ({ value: def.slug, label: def.display_name || def.slug }));
    }, [enableCustomFields, customFieldIds, customFieldStore.items]);

    const roleOptions = useMemo(
      () => TRANSITION_ROLES.map((role) => ({ value: role, label: role })),
      [],
    );

    const patch = (index: number, changes: Partial<TaskTransitionRule>) => {
      onChange(value.map((rule, i) => (i === index ? { ...rule, ...changes } : rule)));
    };

    const toggleTarget = (index: number, target: string) => {
      const current = value[index]?.to_statuses || [];
      const next = current.includes(target)
        ? current.filter((status) => status !== target)
        : [...current, target];
      // Keep targets in lifecycle order so the collapsed row reads the same way
      // however the admin clicked them in.
      const ordered = BASE_TASK_STATUSES.filter((status) => next.includes(status));
      patch(index, { to_statuses: [...ordered] });
    };

    const toggleListValue = (index: number, key: 'required_fields' | 'required_roles', item: string) => {
      const current = value[index]?.[key] || [];
      const next = current.includes(item)
        ? current.filter((entry) => entry !== item)
        : [...current, item];
      patch(index, key === 'required_fields' ? { required_fields: next } : { required_roles: next });
    };

    const addRule = () => {
      const used = new Set(value.map((rule) => rule.from_status));
      const from = BASE_TASK_STATUSES.find((status) => !used.has(status)) || BASE_TASK_STATUSES[0];
      onChange([...value, emptyTransitionRule(from)]);
      setExpandedIndex(value.length);
    };

    const removeRule = () => {
      if (pendingRemoval === null) return;
      onChange(value.filter((_, i) => i !== pendingRemoval));
      setExpandedIndex(null);
      setPendingRemoval(null);
    };

    const warnings: string[] = [];
    if (isStrict) {
      if (analysis.unreachable.length > 0) {
        warnings.push(
          t('task.lifecycle.transitions.warnUnreachable', {
            statuses: analysis.unreachable.map(label).join(', '),
            start: label(analysis.initialStatus),
          }),
        );
      }
      if (!analysis.hasCompletionPath) {
        warnings.push(
          t('task.lifecycle.transitions.warnNoCompletion', {
            start: label(analysis.initialStatus),
            completion: analysis.completionTargets.map(label).join(', '),
          }),
        );
      }
      if (analysis.emptyRuleIndexes.length > 0) {
        warnings.push(
          t('task.lifecycle.transitions.warnEmptyRule', {
            statuses: analysis.emptyRuleIndexes
              .map((index) => label(value[index]?.from_status || ''))
              .join(', '),
          }),
        );
      }
      if (analysis.duplicateSources.length > 0) {
        warnings.push(
          t('task.lifecycle.transitions.warnDuplicateSource', {
            statuses: analysis.duplicateSources.map(label).join(', '),
          }),
        );
      }
    }

    return (
      <div className="space-y-4">
        {/* Mode — the single most important thing to know about this list. */}
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {isStrict
                  ? t('task.lifecycle.transitions.modeStrict')
                  : t('task.lifecycle.transitions.modeFree')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {isStrict
                  ? t('task.lifecycle.transitions.modeStrictHint')
                  : t('task.lifecycle.transitions.modeFreeHint')}
              </p>
            </div>
            <div className="shrink-0">
              {isStrict ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => setConfirmFree(true)}
                >
                  {t('task.lifecycle.transitions.switchToFree')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={disabled}
                  onClick={() => {
                    onChange(defaultTransitionRules());
                    setExpandedIndex(null);
                  }}
                >
                  {t('task.lifecycle.transitions.startFromDefault')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {isStrict && (
          <>
            {/* What the rows alone cannot show: entry point, dead ends, orphans. */}
            <div className="space-y-2">
              <span
                id="lifecycle-graph-overview"
                className="block text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {t('task.lifecycle.transitions.graphOverview')}
              </span>
              <ul className="flex flex-wrap gap-2" aria-labelledby="lifecycle-graph-overview">
                {analysis.nodes.map((node) => {
                  const unreachable = analysis.unreachable.includes(node);
                  const custom = statusesInCategory(statusDefinitions, node);
                  return (
                    <li
                      key={node}
                      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 ${
                        unreachable ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-background'
                      }`}
                    >
                      {custom.length > 0 && (
                        <span className="flex -space-x-1" aria-hidden="true">
                          {custom.slice(0, 3).map((def) => (
                            <span
                              key={def.slug}
                              className="h-2.5 w-2.5 rounded-full border border-border"
                              style={def.color ? { backgroundColor: def.color } : undefined}
                            />
                          ))}
                        </span>
                      )}
                      <span className="text-sm text-foreground">{label(node)}</span>
                      {node === analysis.initialStatus && (
                        <Badge variant="primary" size="sm">
                          {t('task.lifecycle.transitions.markerStart')}
                        </Badge>
                      )}
                      {analysis.completionTargets.includes(node) && (
                        <Badge variant="success" size="sm">
                          {t('task.lifecycle.transitions.markerCompletion')}
                        </Badge>
                      )}
                      {analysis.terminal.includes(node) && (
                        <Badge variant="secondary" size="sm">
                          {t('task.lifecycle.transitions.markerTerminal')}
                        </Badge>
                      )}
                      {unreachable && (
                        <Badge variant="warning" size="sm">
                          {t('task.lifecycle.transitions.markerUnreachable')}
                        </Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {warnings.length > 0 && (
              <Alert variant="warning" title={t('task.lifecycle.transitions.warningsTitle')}>
                <ul className="list-disc space-y-1 pl-4 text-sm">
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <ul className="space-y-2">
              {value.map((rule, index) => {
                const isExpanded = expandedIndex === index;
                const targets = rule.to_statuses || [];
                const requiredFields = rule.required_fields || [];
                const requiredRoles = rule.required_roles || [];
                const hasApproval = !!rule.approval_pipeline?.id;
                const categoryStatuses = statusesInCategory(statusDefinitions, rule.from_status);

                return (
                  <li key={`${rule.from_status}-${index}`} className="rounded-md border border-border bg-background">
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                      <span className="text-sm font-medium text-foreground">{label(rule.from_status)}</span>
                      <span className="text-muted-foreground" aria-hidden="true">
                        &rarr;
                      </span>
                      {targets.length === 0 ? (
                        <span className="text-sm text-muted-foreground">
                          {t('task.lifecycle.transitions.noTargets')}
                        </span>
                      ) : (
                        <span className="flex flex-wrap items-center gap-1.5">
                          {targets.map((target) => (
                            <span
                              key={target}
                              className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground"
                            >
                              {label(target)}
                            </span>
                          ))}
                        </span>
                      )}

                      <span className="flex flex-wrap items-center gap-1">
                        {rule.requires_comment && (
                          <Badge variant="secondary" size="sm">
                            {t('task.lifecycle.transitions.badgeComment')}
                          </Badge>
                        )}
                        {requiredFields.length > 0 && (
                          <Badge variant="secondary" size="sm">
                            {t('task.lifecycle.transitions.badgeFields', { count: requiredFields.length })}
                          </Badge>
                        )}
                        {requiredRoles.length > 0 && (
                          <Badge variant="secondary" size="sm">
                            {t('task.lifecycle.transitions.badgeRoles', { count: requiredRoles.length })}
                          </Badge>
                        )}
                        {hasApproval && (
                          <Badge variant="primary" size="sm">
                            {t('task.lifecycle.transitions.badgeApproval')}
                          </Badge>
                        )}
                      </span>

                      <div className="ml-auto flex shrink-0 items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={isExpanded}
                          onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        >
                          {isExpanded ? t('common.close') : t('common.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={disabled}
                          onClick={() => setPendingRemoval(index)}
                        >
                          {t('common.remove')}
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="space-y-1 border-t border-border px-3 py-2">
                        <FormRow label={t('task.lifecycle.transitions.fromStatus')} labelWidth="md">
                          <Select
                            value={rule.from_status}
                            onChange={(e) => patch(index, { from_status: e.target.value })}
                            options={statusOptions}
                            disabled={disabled}
                            fullWidth={false}
                            className="w-56"
                            aria-label={t('task.lifecycle.transitions.fromStatus')}
                          />
                        </FormRow>

                        <FormRow
                          label={t('task.lifecycle.transitions.toStatuses')}
                          labelWidth="md"
                          hint={t('task.lifecycle.transitions.toStatusesHint')}
                        >
                          <ChipMultiSelect
                            options={statusOptions.filter((option) => option.value !== rule.from_status)}
                            selected={targets}
                            onToggle={(status) => toggleTarget(index, status)}
                            disabled={disabled}
                          />
                        </FormRow>

                        {categoryStatuses.length > 0 && (
                          <FormRow
                            label={t('task.lifecycle.transitions.mappedStatuses')}
                            labelWidth="md"
                            hint={t('task.lifecycle.transitions.mappedStatusesHint')}
                          >
                            <ul className="flex flex-wrap gap-1.5">
                              {categoryStatuses.map((def) => (
                                <li
                                  key={def.slug}
                                  className="flex items-center gap-1.5 rounded border border-border px-1.5 py-0.5 text-xs text-foreground"
                                >
                                  <span
                                    className="h-2 w-2 rounded-full border border-border"
                                    style={def.color ? { backgroundColor: def.color } : undefined}
                                    aria-hidden="true"
                                  />
                                  {def.display_name || def.slug}
                                </li>
                              ))}
                            </ul>
                          </FormRow>
                        )}

                        <div className="mt-3 space-y-2 border-t border-border pt-3">
                          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t('task.lifecycle.transitions.requirementsSection')}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {t('task.lifecycle.transitions.requirementsHint')}
                          </p>

                          <ToggleSwitch
                            label={t('task.lifecycle.transitions.requiresComment')}
                            description={t('task.lifecycle.transitions.requiresCommentHint')}
                            checked={!!rule.requires_comment}
                            disabled={disabled}
                            onChange={(checked) => patch(index, { requires_comment: checked })}
                            className="py-1.5"
                          />

                          <FormRow
                            label={t('task.lifecycle.transitions.requiredFields')}
                            labelWidth="md"
                            hint={t('task.lifecycle.transitions.requiredFieldsHint')}
                          >
                            <div className="space-y-2">
                              <ChipMultiSelect
                                options={fieldOptions}
                                selected={requiredFields}
                                onToggle={(field) => toggleListValue(index, 'required_fields', field)}
                                disabled={disabled}
                              />
                              {enableCustomFields && (
                                <div className="space-y-1.5 pt-1">
                                  <span className="block text-xs text-muted-foreground">
                                    {t('task.lifecycle.transitions.customFieldsGroup')}
                                  </span>
                                  {customFieldStore.isLoading ? (
                                    <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                                  ) : customFieldStore.error ? (
                                    <p className="text-sm text-destructive">{t('common.loadError')}</p>
                                  ) : (
                                    <ChipMultiSelect
                                      options={customFieldOptions}
                                      selected={requiredFields}
                                      onToggle={(field) => toggleListValue(index, 'required_fields', field)}
                                      emptyLabel={t('task.typeForm.noCustomFields')}
                                      disabled={disabled}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </FormRow>

                          <FormRow
                            label={t('task.lifecycle.transitions.requiredRoles')}
                            labelWidth="md"
                            hint={t('task.lifecycle.transitions.requiredRolesHint')}
                          >
                            <ChipMultiSelect
                              options={roleOptions}
                              selected={requiredRoles}
                              onToggle={(role) => toggleListValue(index, 'required_roles', role)}
                              disabled={disabled}
                            />
                          </FormRow>

                          <FormRow
                            label={t('task.lifecycle.transitions.approvalPipeline')}
                            labelWidth="md"
                            hint={t('task.lifecycle.transitions.approvalPipelineHint')}
                          >
                            <div className="space-y-2">
                              <PipelinePicker
                                value={rule.approval_pipeline?.id || ''}
                                onChange={(pipelineId) =>
                                  patch(index, {
                                    // The service only reads the id off this
                                    // reference, but it is stored as an Entity —
                                    // so it is written as a well-formed one.
                                    approval_pipeline: pipelineId
                                      ? makeEntity(pipelineId, 'pipeline', scope)
                                      : undefined,
                                  })
                                }
                                disabled={disabled}
                                className="w-full max-w-sm"
                              />
                              {hasApproval && (
                                <p className="text-xs text-muted-foreground">
                                  {t('task.lifecycle.transitions.approvalPipelineActive')}
                                </p>
                              )}
                            </div>
                          </FormRow>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {isStrict
              ? t('task.lifecycle.transitions.addRuleHint')
              : t('task.lifecycle.transitions.addRuleFromFreeHint')}
          </p>
          <Button size="sm" variant="secondary" disabled={disabled} onClick={addRule}>
            {t('task.lifecycle.transitions.addRule')}
          </Button>
        </div>

        <ConfirmDialog
          open={pendingRemoval !== null}
          title={t('task.lifecycle.transitions.removeRuleTitle')}
          description={t('task.lifecycle.transitions.removeRuleDescription', {
            status:
              pendingRemoval !== null ? label(value[pendingRemoval]?.from_status || '') : '',
          })}
          confirmLabel={t('common.remove')}
          destructive
          onConfirm={removeRule}
          onCancel={() => setPendingRemoval(null)}
        />

        <ConfirmDialog
          open={confirmFree}
          title={t('task.lifecycle.transitions.switchToFreeTitle')}
          description={t('task.lifecycle.transitions.switchToFreeDescription', {
            count: value.length,
          })}
          confirmLabel={t('task.lifecycle.transitions.switchToFree')}
          destructive
          onConfirm={() => {
            onChange([]);
            setExpandedIndex(null);
            setConfirmFree(false);
          }}
          onCancel={() => setConfirmFree(false)}
        />
      </div>
    );
  },
);

export default TaskTypeTransitionsEditor;
